import React, { useEffect, useMemo, useState } from 'react';
import { PublicLayout } from '@/components/PublicLayout';
import { StepperHeader } from '@/components/StepperHeader';
import {
  CalendarDays,
  Clock,
  Users,
  FileText,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Zap,
  MessageCircle,
  MapPin,
  Navigation,
  Mail,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toIsoDay, fmtDay } from '@/lib/format';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { TID } from '@/lib/testIds';
import { useSettings } from '@/contexts/SettingsContext';
import { useNavigate, Link } from 'react-router-dom';

const STEPS = [
  { key: 'date', label: 'Date', icon: CalendarDays },
  { key: 'time', label: 'Time', icon: Clock },
  { key: 'guests', label: 'Guests', icon: Users },
  { key: 'details', label: 'Details', icon: FileText },
];

const buildWhatsAppHref = (settings, message) => {
  const phone = String(settings?.whatsapp_number || '').replace(/[^0-9]/g, '');
  if (!phone) return null;
  const text = encodeURIComponent(message);
  return `https://wa.me/${phone}?text=${text}`;
};

const buildDirectionsHref = (settings) =>
  settings?.social_links?.google_directions ||
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    settings?.address || 'Smithy Browns Milton Keynes',
  )}`;

export default function BookTablePage() {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [guests, setGuests] = useState(2);
  const [details, setDetails] = useState({ name: '', email: '', phone: '', special_requests: '' });
  const [slots, setSlots] = useState([]);
  const [slotInfo, setSlotInfo] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const maxParty = settings?.max_party_size || 12;

  useEffect(() => {
    if (!date) { setSlots([]); return; }
    const iso = toIsoDay(date);
    setLoadingSlots(true);
    api.get(`/reservations/slots?date=${iso}`).then(({ data }) => {
      setSlots(data.slots || []);
      setSlotInfo(data);
      setTime(null);
    }).catch(() => toast.error('Could not load time slots')).finally(() => setLoadingSlots(false));
  }, [date]);

  const lunchSlots = slots.filter((s) => parseInt(s.time.split(':')[0], 10) < 17);
  const dinnerSlots = slots.filter((s) => parseInt(s.time.split(':')[0], 10) >= 17);

  const canNext = () => {
    if (step === 0) return !!date;
    if (step === 1) return !!time;
    if (step === 2) return guests > 0;
    if (step === 3) return details.name && details.email && details.phone;
    return false;
  };

  const submit = async () => {
    if (!date || !time) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/reservations', {
        date: toIsoDay(date),
        time,
        party_size: guests,
        customer: { name: details.name, email: details.email, phone: details.phone },
        special_requests: details.special_requests || undefined,
      });
      setConfirmation(data);
      toast.success('Table booked!');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not book your table');
    } finally {
      setSubmitting(false);
    }
  };

  const largePartyWhatsApp = buildWhatsAppHref(
    settings,
    `Hi Smithy Browns, I'd like to book a table for a larger group (${maxParty}+ guests).`,
  );

  if (confirmation) {
    return (
      <Confirmation
        confirmation={confirmation}
        settings={settings}
        onHome={() => navigate('/')}
      />
    );
  }

  return (
    <PublicLayout>
      <div data-testid={TID.book.page} className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 grid lg:grid-cols-[360px_1fr] gap-8">
        {/* Left intro — simplified per Phase 10 */}
        <div className="hidden lg:block">
          <h1 className="font-display text-5xl text-white leading-none">BOOK A TABLE</h1>
          <h2 className="font-display text-3xl sb-text-gradient-gold leading-none mt-1">AT SMITHY BROWNS</h2>
          <p className="text-white/75 text-sm mt-4 max-w-xs">
            Choose your date, time and number of guests and we&rsquo;ll take care of the rest.
          </p>
          <ul className="mt-6 space-y-3 text-sm text-white/85">
            <li className="flex items-center gap-2">
              <span className="h-7 w-7 rounded-full bg-[hsl(var(--sb-gold))]/15 border border-[hsl(var(--sb-gold))]/40 grid place-items-center">
                <Zap size={14} className="text-[hsl(var(--sb-gold))]" />
              </span>
              Quick &amp; easy
            </li>
            <li className="flex items-center gap-2">
              <span className="h-7 w-7 rounded-full bg-[hsl(var(--sb-gold))]/15 border border-[hsl(var(--sb-gold))]/40 grid place-items-center">
                <CheckCircle2 size={14} className="text-[hsl(var(--sb-gold))]" />
              </span>
              Instant confirmation
            </li>
          </ul>
          {largePartyWhatsApp && (
            <a
              href={largePartyWhatsApp}
              target="_blank"
              rel="noreferrer"
              data-testid="booking-large-party-whatsapp"
              className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#25D366] text-white font-semibold text-sm hover:bg-[#1FB855] transition-colors"
            >
              <MessageCircle size={14} /> Larger group? WhatsApp us
            </a>
          )}
        </div>

        {/* Right wizard */}
        <div data-testid={TID.book.wizard} className="sb-card p-5 sm:p-7">
          <div className="font-display text-2xl text-white mb-1">BOOK A TABLE</div>
          <StepperHeader steps={STEPS} current={step} />

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="date" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}>
                <div className="uppercase text-xs tracking-widest text-white/55 mb-3">Select date</div>
                <CalendarGrid value={date} onChange={setDate} month={calMonth} setMonth={setCalMonth} minDate={today} settings={settings} />
              </motion.div>
            )}
            {step === 1 && (
              <motion.div key="time" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}>
                <div className="uppercase text-xs tracking-widest text-white/55">Choose your time</div>
                {loadingSlots ? (
                  <div className="py-10 text-center text-white/60">Loading slots…</div>
                ) : slotInfo?.closed ? (
                  <div className="py-10 text-center text-white/70">{slotInfo.reason}. Please pick another date.</div>
                ) : (
                  <div data-testid={TID.book.time} className="mt-3 space-y-5">
                    {lunchSlots.length > 0 && (
                      <div>
                        <div className="text-[hsl(var(--sb-green))] uppercase text-xs tracking-widest mb-2">Lunch</div>
                        <SlotGrid slots={lunchSlots} time={time} setTime={setTime} />
                      </div>
                    )}
                    {dinnerSlots.length > 0 && (
                      <div>
                        <div className="text-[hsl(var(--sb-green))] uppercase text-xs tracking-widest mb-2">Dinner</div>
                        <SlotGrid slots={dinnerSlots} time={time} setTime={setTime} />
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}
            {step === 2 && (
              <motion.div key="guests" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}>
                <div className="uppercase text-xs tracking-widest text-white/55 mb-3">Number of guests</div>
                <div data-testid={TID.book.guests} className="grid grid-cols-4 gap-2">
                  {Array.from({ length: maxParty }).map((_, i) => {
                    const n = i + 1;
                    return (
                      <button
                        key={n}
                        onClick={() => setGuests(n)}
                        className={`h-14 rounded-md border text-base font-semibold transition-colors ${
                          guests === n
                            ? 'bg-[hsl(var(--sb-green))] text-black border-[hsl(var(--sb-green))]'
                            : 'bg-[hsl(var(--sb-surface))] text-white border-[hsl(var(--sb-border))] hover:border-white/30'
                        }`}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
                {largePartyWhatsApp ? (
                  <a
                    href={largePartyWhatsApp}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="booking-large-party-whatsapp-inline"
                    className="mt-4 inline-flex items-center gap-2 text-sm text-[#25D366] hover:underline"
                  >
                    <MessageCircle size={14} /> Larger group? Message us on WhatsApp →
                  </a>
                ) : (
                  <p className="text-xs text-white/55 mt-3">Larger groups? Please give us a call.</p>
                )}
              </motion.div>
            )}
            {step === 3 && (
              <motion.div key="details" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}>
                <div className="uppercase text-xs tracking-widest text-white/55 mb-3">Your details</div>
                <div data-testid={TID.book.details} className="space-y-3">
                  <Field label="Name *" value={details.name} onChange={(v) => setDetails({ ...details, name: v })} />
                  <Field label="Email address *" type="email" value={details.email} onChange={(v) => setDetails({ ...details, email: v })} />
                  <Field label="Phone number *" value={details.phone} onChange={(v) => setDetails({ ...details, phone: v })} testid={TID.book.phone} />
                  <Field label="Special requests (optional)" textarea value={details.special_requests} onChange={(v) => setDetails({ ...details, special_requests: v })} placeholder="Window seat if possible, allergies, occasion, etc." />
                </div>
                <div className="mt-4 text-xs text-white/65 sb-card p-3">
                  <div className="text-[hsl(var(--sb-gold))] uppercase tracking-widest text-[10px] mb-1">Booking summary</div>
                  <div className="text-white">{date ? fmtDay(toIsoDay(date)) : ''} at {time} · {guests} guest{guests === 1 ? '' : 's'}</div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center justify-between mt-6">
            <button
              data-testid={TID.book.back}
              disabled={step === 0}
              onClick={() => setStep(step - 1)}
              className="h-11 px-4 rounded-md border border-[hsl(var(--sb-border))] text-white/85 disabled:opacity-40 inline-flex items-center gap-1"
            >
              <ChevronLeft size={16} />Back
            </button>
            {step < 3 ? (
              <button
                data-testid={TID.book.next}
                disabled={!canNext()}
                onClick={() => setStep(step + 1)}
                className="h-11 px-6 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold disabled:opacity-40 inline-flex items-center gap-1"
              >
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <button
                data-testid={TID.book.confirm}
                disabled={!canNext() || submitting}
                onClick={submit}
                className="h-11 px-6 rounded-md bg-[hsl(var(--sb-green))] text-black font-semibold disabled:opacity-40 inline-flex items-center gap-1"
              >
                {submitting ? 'Confirming…' : 'Confirm booking'}<ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

const SlotGrid = ({ slots, time, setTime }) => (
  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
    {slots.map((s) => (
      <button
        key={s.time}
        disabled={!s.available}
        onClick={() => setTime(s.time)}
        className={`h-10 rounded-md text-sm font-medium border transition-colors ${
          time === s.time
            ? 'bg-[hsl(var(--sb-green))] text-black border-[hsl(var(--sb-green))]'
            : s.available
            ? 'bg-[hsl(var(--sb-surface))] text-white border-[hsl(var(--sb-border))] hover:border-white/30'
            : 'bg-transparent text-white/30 border-[hsl(var(--sb-border))]/60 line-through'
        }`}
      >
        {s.time}
      </button>
    ))}
  </div>
);

const Field = ({ label, value, onChange, type = 'text', placeholder, textarea, testid }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-widest text-white/55">{label}</span>
    {textarea ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={placeholder}
        className="mt-1 w-full p-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-focus))]"
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full h-11 px-3 rounded-md bg-[hsl(var(--sb-surface))] border border-[hsl(var(--sb-border))] text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--sb-focus))]"
      />
    )}
  </label>
);

const CalendarGrid = ({ value, onChange, month, setMonth, minDate, settings }) => {
  const monthLabel = month.toLocaleString('en-GB', { month: 'long', year: 'numeric' });
  const firstDayIndex = (() => {
    const d = new Date(month.getFullYear(), month.getMonth(), 1);
    return (d.getDay() + 6) % 7;
  })();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const days = Array.from({ length: 42 }, (_, i) => {
    const dayNum = i - firstDayIndex + 1;
    if (dayNum < 1 || dayNum > daysInMonth) return null;
    return new Date(month.getFullYear(), month.getMonth(), dayNum);
  });
  const closedDates = settings?.closed_dates || [];
  const closedDayOfWeek = (settings?.opening_hours || []).filter((h) => h.closed).map((h) => h.day);
  const dowMap = { 1: 'mon', 2: 'tue', 3: 'wed', 4: 'thu', 5: 'fri', 6: 'sat', 0: 'sun' };
  return (
    <div data-testid={TID.book.date} className="sb-card p-4">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="h-9 w-9 grid place-items-center text-[hsl(var(--sb-gold))]"><ChevronLeft size={18} /></button>
        <div className="font-display text-xl text-white">{monthLabel}</div>
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="h-9 w-9 grid place-items-center text-[hsl(var(--sb-gold))]"><ChevronRight size={18} /></button>
      </div>
      <div className="grid grid-cols-7 text-[11px] uppercase tracking-widest text-white/55 mb-2">
        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d) => <div key={d} className="text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const iso = toIsoDay(d);
          const isClosed = closedDates.includes(iso) || closedDayOfWeek.includes(dowMap[d.getDay()]);
          const isPast = d < minDate;
          const selected = value && toIsoDay(value) === iso;
          const disabled = isClosed || isPast;
          return (
            <button
              key={i}
              disabled={disabled}
              onClick={() => onChange(d)}
              className={`h-10 rounded-md text-sm font-medium border transition-colors ${
                selected
                  ? 'bg-[hsl(var(--sb-green))] text-black border-[hsl(var(--sb-green))]'
                  : disabled
                  ? 'bg-transparent text-white/25 border-transparent'
                  : 'bg-transparent text-white border-transparent hover:border-[hsl(var(--sb-border))] hover:bg-white/5'
              }`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Confirmation = ({ confirmation, settings, onHome }) => {
  const directionsHref = buildDirectionsHref(settings);
  const whatsappHref = buildWhatsAppHref(
    settings,
    `Hi Smithy Browns, this is ${confirmation.customer?.name || ''} — reference ${confirmation.reference}.`,
  );
  const emailSent = confirmation.email_sent === true;

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <div className="sb-card p-6 sm:p-8 text-center">
          <CheckCircle2 size={56} className="mx-auto text-[hsl(var(--sb-green))]" />
          <div className="text-[hsl(var(--sb-green))] uppercase text-xs tracking-widest mt-3">Booking confirmed</div>
          <h1 className="font-display text-3xl sm:text-4xl text-white mt-1">
            THANKS {(confirmation.customer?.name || '').toUpperCase()}!
          </h1>
          <p className="text-white/75 text-sm mt-1">Your table has been booked.</p>

          <div className="my-6 mx-auto inline-block px-5 py-3 rounded-md border border-dashed border-[hsl(var(--sb-gold))]/50">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Reference Number</div>
            <div
              data-testid={TID.book.reference}
              className="font-display text-3xl text-[hsl(var(--sb-gold))]"
            >
              {confirmation.reference}
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 max-w-md mx-auto">
            <div className="sb-card p-3">
              <div className="text-[10px] uppercase text-white/55">Date</div>
              <div className="text-white">{fmtDay(confirmation.date)}</div>
            </div>
            <div className="sb-card p-3">
              <div className="text-[10px] uppercase text-white/55">Time</div>
              <div className="text-white">{confirmation.time}</div>
            </div>
            <div className="sb-card p-3">
              <div className="text-[10px] uppercase text-white/55">Guests</div>
              <div className="text-white">{confirmation.party_size}</div>
            </div>
          </div>

          {/* Location */}
          <div className="mt-6 sb-card p-4 text-left">
            <div className="flex items-center gap-2 text-[hsl(var(--sb-gold))]">
              <MapPin size={16} />
              <span className="uppercase text-[10px] tracking-widest">Where to find us</span>
            </div>
            <div className="text-white text-sm mt-1">{settings?.address}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              <a
                href={directionsHref}
                target="_blank"
                rel="noreferrer"
                data-testid="confirmation-directions"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[hsl(var(--sb-gold))] text-black font-semibold text-sm hover:bg-[hsl(var(--sb-gold-2))] transition-colors"
              >
                <Navigation size={14} /> Get Directions
              </a>
              {whatsappHref && (
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="confirmation-whatsapp"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#25D366] text-white font-semibold text-sm hover:bg-[#1FB855] transition-colors"
                >
                  <MessageCircle size={14} /> WhatsApp Us
                </a>
              )}
            </div>
          </div>

          {emailSent ? (
            <p className="mt-5 inline-flex items-center gap-2 text-xs text-[hsl(var(--sb-green))]">
              <Mail size={12} /> A confirmation email has been sent to {confirmation.customer?.email}
            </p>
          ) : (
            <p className="mt-5 text-xs text-white/55">
              Please save your reference number — a confirmation email couldn&rsquo;t be sent this time.
            </p>
          )}

          <div className="mt-6 flex gap-2 justify-center">
            <Link
              to="/menu"
              className="inline-flex items-center gap-1 h-11 px-5 rounded-md border border-[hsl(var(--sb-border))] text-white hover:border-white/40"
            >
              View Menu
            </Link>
            <button
              onClick={onHome}
              className="inline-flex items-center gap-1 h-11 px-5 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold"
            >
              Back to home
            </button>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};
